import { useState, useEffect } from "react";
import { db } from "../firebase/config";
import { doc, onSnapshot, collection } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { fmtDT } from "../crm/dateFormat";

const STATUS_DOC = doc(db, "backupStatus", "latest");
const MANIFEST_COLL = collection(db, "backupManifest");
const PROGRESS_DOC = doc(db, "backupProgress", "current");
const DRIVE_FOLDERS_COLL = collection(db, "backupDriveFolders");

const cardStyle = {
  background: "#1b1d27",
  border: "1px solid #2a2d3a",
  borderRadius: 10,
  padding: 16,
  marginBottom: 12,
};

const buttonStyle = {
  padding: "8px 14px",
  border: "none",
  borderRadius: 8,
  background: "#6c63ff",
  color: "#fff",
  cursor: "pointer",
  fontSize: 13,
};

const dangerButtonStyle = { ...buttonStyle, background: "#c0392b" };

// Correct account for each project, for DISPLAY only (fixes the "Last run"
// grouping regardless of which account the nightly backup actually ran
// under — the real IAM cause is tracked separately).
const PROJECT_ACCOUNT_MAP = {
  "abc-demo-2fd7f": "adyantam@gmail.com",
  "abc-saas-test": "adyantam@gmail.com",
  "abc-dashboard001": "layakarudu@gmail.com",
  "abc-saas-prod": "layakarudu@gmail.com",
  "abc-invoice-system": "layakarudu@gmail.com",
};

// Fixed left-to-right column order.
const ACCOUNT_ORDER = ["adyantam@gmail.com", "layakarudu@gmail.com"];

function regroupAccountsForDisplay(accounts) {
  const byEmail = {};
  for (const email of ACCOUNT_ORDER) {
    byEmail[email] = { email, projects: [], error: null };
  }
  for (const acc of accounts) {
    if (acc.error && byEmail[acc.email]) {
      byEmail[acc.email].error = acc.error;
    }
    for (const p of acc.projects) {
      const correctEmail = PROJECT_ACCOUNT_MAP[p.projectId] || acc.email;
      if (!byEmail[correctEmail]) {
        byEmail[correctEmail] = { email: correctEmail, projects: [], error: null };
      }
      byEmail[correctEmail].projects.push(p);
    }
  }
  return Object.values(byEmail).filter((a) => a.projects.length > 0 || a.error);
}

export default function BackupRestoreScreen() {
  const [status, setStatus] = useState(null);
  const [manifest, setManifest] = useState([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [backupsByKey, setBackupsByKey] = useState({});
  const [progress, setProgress] = useState(null);
  const [driveFolders, setDriveFolders] = useState([]);
  const [expandedKey, setExpandedKey] = useState(null); // "projectId::databaseId" currently expanded, or null

  const toggleExpanded = (key) => setExpandedKey((prev) => (prev === key ? null : key));

  useEffect(() => {
    const unsub1 = onSnapshot(STATUS_DOC, (snap) => setStatus(snap.exists() ? snap.data() : null));
    const unsub2 = onSnapshot(MANIFEST_COLL, (snap) => setManifest(snap.docs.map((d) => d.data())));
    const unsub3 = onSnapshot(PROGRESS_DOC, (snap) => setProgress(snap.exists() ? snap.data() : null));
    const unsub4 = onSnapshot(DRIVE_FOLDERS_COLL, (snap) => setDriveFolders(snap.docs.map((d) => d.data())));
    return () => {
      unsub1();
      unsub2();
      unsub3();
      unsub4();
    };
  }, []);

  const functions = getFunctions(undefined, "asia-south1");
  const listBackupsFn = httpsCallable(functions, "listBackups");
  const restoreBackupFn = httpsCallable(functions, "restoreBackup", { timeout: 540000 });
  const runBackupNowFn = httpsCallable(functions, "runBackupNow", { timeout: 540000 });

  const backupRunning = busy || progress?.running;

  // FIX (backup silent-failure issue, 21 Sept 2026): if a run has said
  // "running: true" for more than 20 minutes with no update, it almost
  // certainly crashed silently (exactly what happened on 21 Sept — a
  // scheduledBackup crash left this frozen on "running" for 7+ hours
  // with no visible warning). Pure client-side check, no new writes.
  const STALE_RUN_MINUTES = 20;
  const runIsStale =
    progress?.running &&
    progress?.startedAt &&
    Date.now() - new Date(progress.startedAt).getTime() > STALE_RUN_MINUTES * 60 * 1000;

  const runBackupNow = async () => {
    setMessage("");
    try {
      await runBackupNowFn();
      setMessage("Backup run complete — see updated status above.");
    } catch (err) {
      setMessage(`Backup run failed: ${err.message}`);
    }
  };

  const loadBackupsFor = async (m) => {
    const key = `${m.projectId}__${m.databaseId}`;
    const res = await listBackupsFn({ accountEmail: m.accountEmail, projectId: m.projectId, databaseId: m.databaseId });
    setBackupsByKey((prev) => ({ ...prev, [key]: res.data.files }));
  };

  const restoreOne = async (m, file) => {
    if (
      !window.confirm(
        `Restore ${m.projectId} (${m.databaseId}) from "${file.name}"?\n\nThis overwrites any documents with matching IDs in that database right now.`
      )
    )
      return;
    setBusy(true);
    setMessage("");
    try {
      await restoreBackupFn({
        mode: "single",
        accountEmail: m.accountEmail,
        projectId: m.projectId,
        databaseId: m.databaseId,
        driveFileId: file.id,
      });
      setMessage(`Restored ${m.projectId} (${m.databaseId}) from ${file.name}.`);
    } catch (err) {
      setMessage(`Restore failed: ${err.message}`);
    }
    setBusy(false);
  };

  const restoreAll = async () => {
    if (
      !window.confirm(
        "Restore EVERY project/database from its latest backup?\n\nThis overwrites current data everywhere with the last backed-up version. This cannot be undone."
      )
    )
      return;
    if (!window.confirm("Are you absolutely sure? Type OK again to confirm.")) return;
    setBusy(true);
    setMessage("");
    try {
      const res = await restoreBackupFn({ mode: "all" });
      const { restored, errors } = res.data;
      setMessage(
        `Restored ${restored.length} database(s).` + (errors.length ? ` ${errors.length} failed — see console.` : "")
      );
      if (errors.length) console.error("Restore errors:", errors);
    } catch (err) {
      setMessage(`Restore all failed: ${err.message}`);
    }
    setBusy(false);
  };

  return (
    <div style={{ padding: 20, color: "#e6e9f2" }}>
      <h2 style={{ marginTop: 0 }}>🗄 Backup & Restore</h2>
      <p style={{ color: "#aab2c5", fontSize: 13 }}>
        Backups run automatically every night at 2 AM and are saved to Google Drive. You can also run one on demand
        below, and use restore if you need to bring a database back to a previous state.
      </p>

      {driveFolders.length > 0 && (
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          {driveFolders.map((f) => (
            <a
              key={f.email}
              href={f.driveUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ ...buttonStyle, textDecoration: "none", display: "inline-block" }}
            >
              Open Drive ({f.email})
            </a>
          ))}
        </div>
      )}

      <div style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <strong>Last run</strong>
          <button style={buttonStyle} disabled={backupRunning} onClick={runBackupNow}>
            {backupRunning ? "Running…" : "Run Backup Now"}
          </button>
        </div>

        {backupRunning && progress && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, color: "#aab2c5", marginBottom: 6 }}>
              {progress.currentLabel ||
                (progress.total
                  ? `Backing up… ${progress.completed?.length || 0} of ${progress.total}`
                  : "Starting…")}
            </div>
            <div style={{ background: "#2a2d3a", borderRadius: 6, height: 8, overflow: "hidden" }}>
              <div
                style={{
                  background: "#6c63ff",
                  height: "100%",
                  width: progress.total
                    ? `${Math.min(100, ((progress.completed?.length || 0) / progress.total) * 100)}%`
                    : "15%",
                  transition: "width 0.3s ease",
                }}
              />
            </div>
            {progress.completed && progress.completed.length > 0 && (
              <div style={{ marginTop: 8, fontSize: 12, color: "#aab2c5" }}>
                {progress.completed.map((c, i) => (
                  <div key={i}>
                    {c.status === "ok" ? "✅" : "❌"} {c.projectId} ({c.databaseId})
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!backupRunning && status ? (
          <div style={{ fontSize: 13, marginTop: 8 }}>
            <div style={{ color: "#aab2c5" }}>
              {fmtDT(status.startedAt)} → {fmtDT(status.finishedAt)}
            </div>
            <div style={{ display: "flex", gap: 24, marginTop: 12, flexWrap: "wrap" }}>
              {regroupAccountsForDisplay(status.accounts).map((acc) => (
                <div key={acc.email} style={{ flex: "1 1 260px", minWidth: 220 }}>
                  <div style={{ color: "#8f96ff", marginBottom: 4 }}>{acc.email}</div>
                  {acc.error && (
                    <div style={{ display: "flex", gap: 8, padding: "4px 0", color: "#ff9d9d" }}>
                      <span>❌</span>
                      <span>Account-level failure — {acc.error}</span>
                    </div>
                  )}
                  {acc.projects.map((p, i) => {
                    const key = `${p.projectId}::${p.databaseId}`;
                    const isOpen = expandedKey === key;
                    return (
                      <div key={i}>
                        <div
                          onClick={() => toggleExpanded(key)}
                          style={{ display: "flex", gap: 8, padding: "4px 0", cursor: "pointer" }}
                          title="Click for details"
                        >
                          <span>{p.status === "ok" ? "✅" : "❌"}</span>
                          <span>
                            {p.projectId} {p.databaseId ? `(${p.databaseId})` : ""}
                          </span>
                          {p.error && <span style={{ color: "#e08080" }}>— {p.error}</span>}
                          <span style={{ color: "#6c63ff", marginLeft: "auto" }}>{isOpen ? "▲ Hide" : "▼ Details"}</span>
                        </div>
                        {isOpen && (
                          <div
                            style={{
                              background: "#14151c",
                              border: "1px solid #2a2d3a",
                              borderRadius: 6,
                              padding: 10,
                              margin: "4px 0 8px 24px",
                              fontSize: 12,
                              color: "#aab2c5",
                            }}
                          >
                            {p.startedAt && p.finishedAt && (
                              <div>
                                Ran {new Date(p.startedAt).toLocaleTimeString()} → {new Date(p.finishedAt).toLocaleTimeString()}
                              </div>
                            )}
                            {p.status === "ok" ? (
                              <>
                                {p.fileName && <div>File: {p.fileName}</div>}
                                {p.sizeBytes != null && <div>Size: {Math.round(p.sizeBytes / 1024)} KB</div>}
                              </>
                            ) : (
                              <>
                                <div style={{ color: "#ff9d9d", marginTop: 4 }}>Full error detail:</div>
                                <pre
                                  style={{
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                    margin: "4px 0 0 0",
                                    color: "#e08080",
                                  }}
                                >
                                  {p.errorDetail || p.error}
                                </pre>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        ) : !backupRunning && !status ? (
          <div style={{ color: "#aab2c5", fontSize: 13, marginTop: 8 }}>No backup run recorded yet.</div>
        ) : null}
      </div>

      <div style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <strong>Restore</strong>
          <button style={dangerButtonStyle} disabled={busy || backupRunning} onClick={restoreAll}>
            Restore ALL (latest backups)
          </button>
        </div>

        {manifest.map((m) => {
          const key = `${m.projectId}__${m.databaseId}`;
          const files = backupsByKey[key];
          return (
            <div key={key} style={{ borderTop: "1px solid #2a2d3a", marginTop: 12, paddingTop: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>
                  {m.projectId} ({m.databaseId}) — {m.accountEmail}
                </span>
                <button style={buttonStyle} disabled={busy || backupRunning} onClick={() => loadBackupsFor(m)}>
                  Show backups
                </button>
              </div>
              {files && (
                <div style={{ marginTop: 8 }}>
                  {files.map((f) => (
                    <div
                      key={f.id}
                      style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0" }}
                    >
                      <span>
                        {f.name} — {fmtDT(f.createdTime)}
                      </span>
                      <button style={buttonStyle} disabled={busy || backupRunning} onClick={() => restoreOne(m, f)}>
                        Restore this
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {runIsStale && (
        <div style={{ ...cardStyle, borderColor: "#e08080", color: "#ff9d9d" }}>
          ⚠️ No update in over {STALE_RUN_MINUTES} minutes — this run likely failed silently.
          {progress?.error ? ` Last recorded error: ${progress.error}` : " Check Cloud Functions logs, or try Run Backup Now."}
        </div>
      )}

      {!backupRunning && status?.error && (
        <div style={{ ...cardStyle, borderColor: "#e08080", color: "#ff9d9d" }}>
          ⚠️ Last run failed: {status.error}
        </div>
      )}

      {message && <div style={{ ...cardStyle, color: "#8fe08f" }}>{message}</div>}
    </div>
  );
}